import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import { buscarDniPorNombres, buscarTelefonoPorDni, buscarTelefonosReniec } from "../services/seeker.service";
import { guardarTelefono } from "../services/expedientes3.service";
/* ===================== TYPE EXPEDIENTE ===================== */

interface Expediente {
  id: number;
  nro_expediente: string;
  tipo_expediente: string;
  estado: string;
  tipo_estado?: string;

  organo_jurisdiccional: string;

  demandante: string;
  demandados: string | null;
  partes_procesales?: string;

  fecha_ingreso: string;
  materia: string;

  telefono?: string | null;
  dni?: string | null;
  monto?: number | null;

  observaciones?: string | null;
  color?: string | null;
}
/* ===================== INTERFACES ===================== */


interface Props {
  open: boolean;
  expediente: Expediente | null;
  onClose: () => void;
  onTelefonoGuardado?: () => void; // 👈 esto
}

interface Persona {
  documento: string;
  paterno: string;
  materno: string;
  nombres: string;
  nacimiento: string;
  edad: string;
  ubicacion: string;
  direccion: string;
  sexo: string;
  estado: string;
  telefono?: string;
}

interface Telefono {
  telefono: string;
  operador?: string;
  periodo?: string;
  titular?: string;
}

/* ===================== COMPONENTE ===================== */

const ExpedienteDetalleModal = ({
  open,
  expediente,
  onClose,
  onTelefonoGuardado // 👈 FALTA ESTO
}: Props) => {
const [telefonoSeleccionado, setTelefonoSeleccionado] = useState("");
const [guardandoTelefono, setGuardandoTelefono] = useState(false);
  const [nombre, setNombres] = useState("");
  const [apPaterno, setApPaterno] = useState("");
  const [apMaterno, setApMaterno] = useState("");
  const [sinMaterno, setSinMaterno] = useState(false);

  const [loading, setLoading] = useState(false);
  const [resultados, setResultados] = useState<Persona[]>([]);
  const [seleccionado, setSeleccionado] = useState<Persona | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [telefonos, setTelefonos] = useState<Telefono[]>([]);
  const [loadingDni, setLoadingDni] = useState(false);

  /* ================== RESET ================== */

  useEffect(() => {
    if (open && expediente) {
      setNombres("");
      setApPaterno("");
      setApMaterno("");
      setSinMaterno(false);
      setResultados([]);
      setSeleccionado(null);
      setTelefonos([]);
      setError(null);
      setLoading(false);
      setLoadingDni(false);
    }
  }, [open, expediente?.id]);

  /* ================== BUSCAR PERSONA ================== */

  const handleBuscar = async () => {

    if (!nombre || !apPaterno) {
      setError("Nombres y apellido paterno son obligatorios");
      return;
    }

    setLoading(true);
    setError(null);
    setResultados([]);
    setSeleccionado(null);
    setTelefonos([]);

    try {

      const nombresQuery = `${nombre} ${apPaterno} ${sinMaterno ? "" : apMaterno}`;

      const resp = await buscarDniPorNombres(nombresQuery);
      const json = resp.data;

      if (json.status === "ok" && json.data && json.data.length > 0) {

        const personas: Persona[] = json.data.map((p: any) => ({
          documento: p.dni,
          paterno: p.ap_pat,
          materno: p.ap_mat,
          nombres: p.nombres,
          nacimiento: p.fecha_nac,
          edad: "",
          ubicacion: p.ubigeo_dir,
          direccion: p.direccion,
          sexo: p.sexo === "1" ? "M" : "F",
          estado: p.est_civil,
          telefono: undefined,
        }));

        setResultados(personas);

        /* SI SOLO HAY 1 RESULTADO -> AUTOSELECCION */

        if (personas.length === 1) {
          setSeleccionado(personas[0]);
        }

      } else {
        setError("No se encontraron coincidencias");
      }

    } catch (err) {
      console.error(err);
      setError("Error al consultar la API");
    } finally {
      setLoading(false);
    }
  };

  /* ================== BUSCAR TELEFONOS POR DNI ================== */
const handleBuscarTelefonosPorDni = async (dni: string) => {

  setLoadingDni(true);
  setTelefonos([]);

  try {

    const [respLocal, respExterna] = await Promise.all([
      buscarTelefonoPorDni(dni),        // OSIPTEL
      buscarTelefonosReniec(dni)  // API externa
    ]);

    /* ===== TELEFONOS OSIPTEL ===== */

    let telefonosLocal = respLocal?.data?.data || [];

    if (!Array.isArray(telefonosLocal)) {
      telefonosLocal = telefonosLocal ? [telefonosLocal] : [];
    }

    /* ===== TELEFONOS EXTERNOS ===== */

    let telefonosExternos = respExterna?.data?.telefonos || [];

    if (!Array.isArray(telefonosExternos)) {
      telefonosExternos = telefonosExternos ? [telefonosExternos] : [];
    }

    const telefonosFormateadosExternos = telefonosExternos.map((t: any) => ({
      telefono: t.telefono,
      operador: "Privado",
      periodo: null,
      titular: t.titular
    }));

    /* ===== UNIR ===== */

    const todos = [
      ...telefonosLocal,
      ...telefonosFormateadosExternos
    ];

    /* ===== ELIMINAR DUPLICADOS ===== */

    const unicos = Array.from(
      new Map(todos.map(t => [t.telefono, t])).values()
    );

    setTelefonos(unicos);

  } catch (err) {

    console.error("Error consultando teléfonos:", err);
    setTelefonos([]);

  } finally {

    setLoadingDni(false);

  }

};

  /* ================== CUANDO SE SELECCIONA PERSONA ================== */

  useEffect(() => {

    if (seleccionado) {

      handleBuscarTelefonosPorDni(seleccionado.documento);

    }

  }, [seleccionado]);

  useEffect(() => {
  if (telefonos.length > 0) {
    setTelefonoSeleccionado(telefonos[0].telefono);
  }
}, [telefonos]);

const handleGuardarTelefono = async () => {
  if (!telefonoSeleccionado || !expediente) return;

  setGuardandoTelefono(true);

  try {
    await guardarTelefono(expediente.id, telefonoSeleccionado);

    alert("Teléfono guardado correctamente");
    onTelefonoGuardado?.();

    // 👇 opcional: cierra modal
    onClose();
  } catch (err) {
    console.error(err);
    alert("Error al guardar teléfono");
  } finally {
    setGuardandoTelefono(false);
  }
};

  /* ===================== RENDER ===================== */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>

      {expediente ? (
        <>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight={600}>
                Detalle del Expediente
              </Typography>

              <Typography color="primary" fontWeight={600}>
                {expediente.nro_expediente}
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent dividers>

            {/* DATOS EXPEDIENTE */}

            <Stack spacing={1.5}>
              <Typography><b>Órgano:</b> {expediente.organo_jurisdiccional}</Typography>
              <Typography><b>Estado:</b> {expediente.estado}</Typography>
              <Typography><b>Tipo:</b> {expediente.tipo_expediente}</Typography>
              <Typography><b>Materia:</b> {expediente.materia}</Typography>
              <Typography>
                <b>Ingreso:</b>{" "}
                {new Date(expediente.fecha_ingreso).toLocaleDateString()}
              </Typography>
              <Typography><b>Demandante:</b> {expediente.demandante}</Typography>
              <Typography><b>Demandados:</b> {expediente.demandados}</Typography>

              {expediente.observaciones && (
                <Typography>
                  <b>Obs:</b> {expediente.observaciones}
                </Typography>
              )}
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* BUSQUEDA RENIEC */}

            <Typography fontWeight={600}>
              Búsqueda RENIEC
            </Typography>

            <Stack spacing={2} mt={2}>

              <TextField
                label="Nombres"
                value={nombre}
                onChange={(e) => setNombres(e.target.value)}
              />

              <TextField
                label="Apellido Paterno"
                value={apPaterno}
                onChange={(e) => setApPaterno(e.target.value)}
              />

              {!sinMaterno && (
                <TextField
                  label="Apellido Materno"
                  value={apMaterno}
                  onChange={(e) => setApMaterno(e.target.value)}
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={sinMaterno}
                    onChange={(e) => setSinMaterno(e.target.checked)}
                  />
                }
                label="No consignar apellido materno"
              />

              <Button
                variant="contained"
                onClick={handleBuscar}
                disabled={loading}
              >
                {loading ? <CircularProgress size={22} /> : "Buscar"}
              </Button>

            </Stack>

            {error && (
              <Typography color="error" mt={2}>
                {error}
              </Typography>
            )}

            {/* RESULTADOS */}

            {resultados.length > 1 && (
              <Box mt={3}>
                <Typography fontWeight={600}>
                  Seleccionar persona
                </Typography>

                {resultados.map((p, i) => (
                  <Box
                    key={i}
                    mt={1}
                    p={1.5}
                    border="1px solid #ddd"
                    borderRadius={2}
                    sx={{ cursor: "pointer" }}
                    onClick={() => setSeleccionado(p)}
                  >
                    <Typography>
                      {p.nombres} {p.paterno} {p.materno}
                    </Typography>

                    <Typography fontSize={14}>
                      DNI: {p.documento}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* PERSONA SELECCIONADA */}

            {seleccionado && (
              <Box mt={3} p={2} bgcolor="#f5f5f5" borderRadius={2}>

                <Typography fontWeight={600}>
                  {seleccionado.nombres} {seleccionado.paterno} {seleccionado.materno}
                </Typography>

                <Typography>DNI: {seleccionado.documento}</Typography>
                <Typography>Dirección: {seleccionado.direccion}</Typography>
                <Typography>Ubicación: {seleccionado.ubicacion}</Typography>
                <Typography>Sexo: {seleccionado.sexo}</Typography>
                <Typography>Estado Civil: {seleccionado.estado}</Typography>

                <Divider sx={{ my: 2 }} />

                <Typography fontWeight={600}>
                  Teléfonos por DNI
                </Typography>

                {loadingDni && <CircularProgress size={20} />}

                {!loadingDni && telefonos.length === 0 && (
                  <Typography>No se encontraron teléfonos</Typography>
                )}

                {!loadingDni &&
                  telefonos.map((t, i) => (
                    <Box
                      key={i}
                      mt={1}
                      p={1.5}
                      border="1px solid #1976d2"
                      borderRadius={1}
                      bgcolor="#e3f2fd"
                      sx={{ cursor: "pointer" }}
                      onClick={() => setTelefonoSeleccionado(t.telefono)}
                    >
                      <Typography>
                        📱 <b>{t.telefono}</b>{" "}
                        {t.operador ? `(${t.operador})` : ""}{" "}
                        {t.periodo ? `- ${t.periodo}` : ""}
                      </Typography>
                    </Box>
                  ))}
                  {telefonos.length > 0 && (
  <Box mt={3}>

    <Divider sx={{ my: 2 }} />

    <Typography fontWeight={600}>
      Guardar teléfono
    </Typography>

    <Stack direction="row" spacing={2} mt={2}>

      <TextField
        label="Teléfono seleccionado"
        fullWidth
        value={telefonoSeleccionado}
        onChange={(e) => setTelefonoSeleccionado(e.target.value)}
      />

      <Button
        variant="contained"
        color="success"
        onClick={handleGuardarTelefono}
        disabled={guardandoTelefono || !telefonoSeleccionado}
      >
        {guardandoTelefono ? (
          <CircularProgress size={20} />
        ) : (
          "Guardar"
        )}
      </Button>

    </Stack>

  </Box>
)}
              </Box>
            )}

          </DialogContent>
        </>
      ) : (
        <DialogContent dividers>
          <Typography>No hay expediente seleccionado</Typography>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ExpedienteDetalleModal;